/** Reads that several parts of a page ask for at the same moment. */

const flying = new Map<string, Promise<unknown>>()

/** One request for everyone who asks while it is on its way: the charts of a
 *  page opening, or the tick, ask for the same lists in the same instant.
 *  Only while it is on its way. Nothing is remembered once it has answered, so
 *  the next read is a fresh one and can never be an old answer. For lists that
 *  are read and not written through this page: a write must see itself, so
 *  what writes keeps its own read. */
export function sharedRead<T>(key: string, ask: () => Promise<T>): Promise<T> {
  const held = flying.get(key) as Promise<T> | undefined
  if (held) return held
  const read = ask().finally(() => { if (flying.get(key) === read) flying.delete(key) })
  flying.set(key, read)
  return read
}

/** How many charts on a running dashboard read their window at once. The rest
 *  wait their turn, so a wall of forty charts on a thirty-day window is spread
 *  out instead of landing on one api process in the same instant. */
export const MAX_CHART_READS = 3

let running = 0
const waiting: (() => void)[] = []

function next() {
  if (running < MAX_CHART_READS) waiting.shift()?.()
}

/** Run `ask` when a place is free. `abandoned` is asked when its turn comes: a
 *  chart that has gone or asked again meanwhile costs the server nothing. */
export function limitedChartRead<T>(ask: () => Promise<T>, abandoned: () => boolean = () => false): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const start = () => {
      if (abandoned()) {
        reject(new Error('This read is no longer wanted.'))
        next()
        return
      }
      running += 1
      ask().then(resolve, reject).finally(() => {
        running -= 1
        next()
      })
    }
    if (running < MAX_CHART_READS) start()
    else waiting.push(start)
  })
}
